// Нормализация документа после каждой транзакции (appendTransaction):
//   - у каждого блока (ID_TYPES) есть уникальный id; при копировании дубликат в изменённой области получает новый id
//     (оригинал сохраняет id - перекрёстные ссылки на него не ломаются; при вырезании/вставке id переносится);
//   - у таблицы stoTable есть название (caption); stoTable без таблицы удаляется.
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { ID_TYPES } from '../schema/constants';
import { newId } from '../schema/util';

const ID_SET = new Set(ID_TYPES);
export const normalizeKey = new PluginKey('stoNormalize');

function changedRanges(trs: readonly Transaction[]): [number, number][] {
  let ranges: [number, number][] = [];
  for (const tr of trs) {
    ranges = ranges.map(([a, b]) => [tr.mapping.map(a, -1), tr.mapping.map(b, 1)] as [number, number]);
    tr.steps.forEach((step, i) => {
      const later = tr.mapping.slice(i + 1);
      step.getMap().forEach((_os, _oe, ns, ne) => { ranges.push([later.map(ns, -1), later.map(ne, 1)]); });
    });
  }
  return ranges;
}

export const NormalizeExtension = Extension.create({
  name: 'stoNormalize',
  addProseMirrorPlugins() {
    return [new Plugin({
      key: normalizeKey,
      appendTransaction: (trs, _old, state) => {
        if (!trs.some((t) => t.docChanged)) return null;
        const ranges = changedRanges(trs);
        const inChanged = (pos: number) => ranges.some(([a, b]) => pos >= a && pos <= b);
        const seen = new Map<string, number>();
        const fixes: { pos: number; node: PMNode }[] = [];
        const tables: { pos: number; node: PMNode }[] = [];
        state.doc.descendants((node, pos) => {
          const t = node.type.name;
          if (t === 'stoTable') tables.push({ pos, node });
          if (!ID_SET.has(t)) return true;
          const id = node.attrs.id as string | null;
          if (!id) { fixes.push({ pos, node }); return true; }
          if (seen.has(id)) {
            // дубликат: новый id получает тот экземпляр, который лежит в изменённой области
            const first = seen.get(id)!;
            if (inChanged(pos) || !inChanged(first)) fixes.push({ pos, node });
            else { fixes.push({ pos: first, node: state.doc.nodeAt(first)! }); seen.set(id, pos); }
          } else seen.set(id, pos);
          return true;
        });
        if (!fixes.length && !tables.some(({ node }) => node.firstChild?.type.name !== 'caption' || !hasTable(node))) return null;
        const tr = state.tr;
        for (const { pos, node } of fixes) tr.setNodeMarkup(pos, undefined, { ...node.attrs, id: newId() });
        // таблицы: название обязательно (4.9.4); обёртка без таблицы - удалить (с конца, чтобы не сбить позиции)
        for (const { pos, node } of [...tables].reverse()) {
          const p = tr.mapping.map(pos);
          if (!hasTable(node)) { tr.delete(p, p + node.nodeSize); continue; }
          if (node.firstChild?.type.name !== 'caption') tr.insert(p + 1, state.schema.nodes.caption.create());
        }
        return tr;
      },
    })];
  },
});

function hasTable(node: PMNode) {
  let ok = false;
  node.forEach((c) => { if (c.type.name === 'table') ok = true; });
  return ok;
}
